/**
 * Multi-Property Favicon Management
 * Provides a mapping of property codes to favicon files.
 */

/**
 * Property favicon configuration.
 * Add new properties here with their corresponding favicon files.
 */
const PROPERTY_FAVICONS = {
  'PMH': 'favicon-PMH.ico',
  'DEMO': 'favicon-DEMO.ico',
  'FITRA': 'favicon-FITRA.ico',
  'PNS': 'favicon-PNS.ico',
  'PKS': 'favicon-PKS.ico',
  'GPS': 'favicon-GPS.ico',
  'PCN': 'favicon-PCN.ico',
  'IZZY': 'favicon-IZZY.ico',
  'RWM': 'favicon-RWM.ico',
  'KRTJ': 'favicon-KRTJ.ico',
  'PMH2': 'favicon-PMH2.ico',
};

/**
 * Gets the favicon filename for a given property code.
 * If the property code is not found or is null, it returns the default favicon.
 * 
 * @param {string} propertyCode - The property code (e.g., 'PMH', 'DEMO').
 * @returns {string} - The corresponding favicon filename (e.g., 'favicon-PMH.ico').
 */
export const getFaviconForProperty = (propertyCode) => {
  if (!propertyCode) {
    return 'favicon.ico';
  }

  const upperPropertyCode = propertyCode.toUpperCase();
  return PROPERTY_FAVICONS[upperPropertyCode] || 'favicon.ico';
};

/**
 * Get the current favicon property from the document
 * @returns {string|null} - The current property code or null
 */
export const getCurrentFaviconProperty = () => {
  if (typeof window === 'undefined') return null;
  
  const faviconLink = document.querySelector('link[rel*="icon"]');
  if (!faviconLink) return null;
  
  const href = faviconLink.href;
  const match = href.match(/favicon-([A-Z0-9]+)\.ico/);
  return match ? match[1] : null;
};

/**
 * Check if favicon needs to be updated for the given property
 * @param {string} propertyCode - The property code to check
 * @returns {boolean} - True if update is needed
 */
export const needsFaviconUpdate = (propertyCode) => {
  const currentProperty = getCurrentFaviconProperty();
  return currentProperty !== propertyCode?.toUpperCase();
};

/**
 * Get list of all available properties
 * @returns {string[]} - Array of property codes
 */
export const getAvailableProperties = () => {
  return Object.keys(PROPERTY_FAVICONS);
};

/**
 * Check if a property has a custom favicon
 * @param {string} propertyCode - The property code to check
 * @returns {boolean} - True if custom favicon exists
 */
export const hasCustomFavicon = (propertyCode) => {
  if (!propertyCode) return false;
  return PROPERTY_FAVICONS.hasOwnProperty(propertyCode.toUpperCase());
};

/**
 * Update favicon for a specific property
 * @param {string} propertyCode - The property code
 * @returns {Promise<boolean>} - True if successful
 */
export const updateFaviconForProperty = async (propertyCode) => {
  if (typeof window === 'undefined') return false;
  
  try {
    const faviconFile = getFaviconForProperty(propertyCode);
    const faviconPath = `/${faviconFile}`;
    
    // Update all favicon links
    const faviconLinks = document.querySelectorAll('link[rel*="icon"]');
    faviconLinks.forEach(link => {
      link.href = faviconPath;
    });
    
    // If no favicon links exist, create one
    if (faviconLinks.length === 0) {
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = faviconPath;
      document.head.appendChild(link);
    }
    
    return true;
  } catch (error) {
    console.error('Error updating favicon:', error);
    return false;
  }
};
